// CTGMTX-10223: Hydration error tracking
import NextErrorComponent from "next/error";
import React, { useEffect, useMemo } from "react";
import App from "next/app";
import ReactDOM from "react-dom/client";
import initializeOIL from "../lib/InitializeOIL";
import { log } from "../lib/utils/commons";
import {
  ThemeOrchestrationProvider,
  ThemeProvider
} from "@design-systems/theme";
import {
  getAppTheme,
  getAppColorScheme,
  getTokensUrlMap,
  isThemeMounted,
  getFallbackThemeUrl
} from "../lib/utils/theme-utils";

// Hydration error tracking: patch hydrateRoot, capture causes/hierarchy, log once to Splunk
if (typeof window !== "undefined") {
  let hasLoggedToSplunk = false;
  let processTimeout = null;
  let capturedErrors = [];
  const seenErrorKeys = new Set();

  window.hydrationerror = {
    pageUrl: window.location.href,
    hasHydrationError: false,
    primaryCause: null,
    cascadingCauses: [],
    errorStack: []
  };

  const NEXT_DYNAMIC_WRAPPER = "LoadableComponent";

  const getPackageFromLine = (line) => {
    const match = line.match(/node_modules\/(@[\w-]+\/[\w-]+|[\w-]+)\//);
    return match ? match[1] : null;
  };

  // Convert PascalCase to kebab-case for matching
  const pascalToKebab = (name) => {
    if (!name) return "";
    return name
      .replace(/([A-Z])/g, "-$1")
      .toLowerCase()
      .replace(/^-/, "");
  };

  // Lookup component full name from hydration registry
  const getFullNameFromRegistry = (componentName) => {
    const registry = window.__HYDRATION_COMPONENTS__;
    if (!registry || !Array.isArray(registry)) return null;

    const kebabName = pascalToKebab(componentName);

    // Find matching block name (e.g., "NavItem" -> "@gwp-components/nav-item")
    for (const blockName of registry) {
      const normalizedBlock = blockName.replace(/^@/, "").toLowerCase();
      const blockParts = normalizedBlock.split("/");
      const blockComponent = blockParts[blockParts.length - 1];

      if (
        blockComponent === kebabName ||
        normalizedBlock.endsWith("/" + kebabName)
      ) {
        return blockName;
      }
    }
    return null;
  };

  // Sync resolution: uses package info from stack trace, then registry lookup
  const resolveFullNameSync = (comp) => {
    // Commented out to verify registry method only
    if (comp.pkg && comp.pkg !== comp.name) {
      comp.resolutionSource = "stack";
      return comp.pkg;
    }
    // Then try registry lookup
    const fromRegistry = getFullNameFromRegistry(comp.name);
    if (fromRegistry) {
      comp.resolutionSource = "registry";
      return fromRegistry;
    }
    comp.resolutionSource = "fallback";
    return comp.name;
  };

  // Fallback: when no next/dynamic (LoadableComponent) wrapper is found in the
  // stack, use the 2nd "at X" frame — the first is typically the anonymous
  // element that mismatched (e.g. `at div`), and the second is the nearest
  // named component that owns it (e.g. `at BodyContent`).
  const getSecondFrameFromComponentStack = (componentStack) => {
    if (!componentStack) return null;
    const frames = [];
    const lines = componentStack.split("\n");
    for (const line of lines) {
      const m = line.match(/at\s+(\S+)\s*(?:\(([^)]+)\))?/);
      if (m) frames.push({ name: m[1], location: m[2] || null, line });
      if (frames.length === 2) break;
    }
    return frames[1] || null;
  };

  // Extract the React minified error code (e.g. "418", "421", "423") from the
  // error message. Returns null when the message doesn't contain one.
  const getReactErrorCode = (message) => {
    if (!message) return null;
    const m = message.match(/#(\d{3,})/);
    return m ? m[1] : null;
  };

  // Parse stack: only components immediately before LoadableComponent
  const parseDynamicComponents = (componentStack) => {
    const lines = componentStack.trim().split("\n");
    const dynamicComponents = [];

    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i];
      const nextLine = lines[i + 1];

      const match = line.match(/at\s+(\w+)/);
      const nextMatch = nextLine.match(/at\s+(\w+)/);

      if (!match || !nextMatch) continue;

      const name = match[1];
      const nextName = nextMatch[1];

      if (
        /^[A-Z]/.test(name) &&
        name !== NEXT_DYNAMIC_WRAPPER &&
        nextName === NEXT_DYNAMIC_WRAPPER
      ) {
        const pkg = getPackageFromLine(line);
        dynamicComponents.push({ name, pkg, line });
      }
    }

    return dynamicComponents;
  };

  const buildHierarchy = (components) => {
    const reversed = [...components].reverse();
    return reversed.map((c) => c.pkg || c.name).join(" => ");
  };

  const getErrorKey = (dynamicComponents, errorMessage) => {
    const componentNames = dynamicComponents.map((c) => c.name).join(",");
    return `${componentNames}|${errorMessage.substring(0, 100)}`;
  };

  const isMeaningfulHierarchy = (hierarchy) => {
    const parts = hierarchy.split(" => ");
    const uniqueParts = new Set(parts);
    return uniqueParts.size > 1;
  };

  // Independent root causes: first captured meaningful error per tree; siblings share tree
  const findIndependentRootCauses = (errors) => {
    if (errors.length === 0) return [];
    const meaningfulErrors = errors.filter((e) =>
      isMeaningfulHierarchy(e.hierarchy)
    );
    const genericErrors = errors.filter(
      (e) => !isMeaningfulHierarchy(e.hierarchy)
    );
    const errorsToProcess =
      meaningfulErrors.length > 0 ? meaningfulErrors : genericErrors;
    const sorted = [...errorsToProcess].sort(
      (a, b) => a.captureOrder - b.captureOrder
    );

    const rootCauses = [];

    for (const error of sorted) {
      const errorParts = error.hierarchy.split(" => ");
      const isRelatedToExisting = rootCauses.some((rc) => {
        const rcParts = rc.hierarchy.split(" => ");
        let commonPrefixLength = 0;
        const minLen = Math.min(errorParts.length, rcParts.length);
        for (let i = 0; i < minLen; i++) {
          if (errorParts[i] === rcParts[i]) {
            commonPrefixLength++;
          } else {
            break;
          }
        }

        const shorterLength = Math.min(errorParts.length, rcParts.length);
        if (commonPrefixLength === shorterLength) return true;
        if (
          commonPrefixLength >= 2 &&
          commonPrefixLength >= shorterLength / 2
        ) {
          return true;
        }

        return false;
      });

      if (!isRelatedToExisting) rootCauses.push(error);
    }

    return rootCauses;
  };

  const processAndLog = async () => {
    if (hasLoggedToSplunk || capturedErrors.length === 0) return;
    hasLoggedToSplunk = true;

    const cid =
      window.appVars?.pageContentId ||
      window.location.pathname
        .split("/")
        .find((p, i, a) => a[i - 1] === "preview" || a[i - 1] === "cas") ||
      "unknown";

    const allResolvedErrors = [];
    for (const errorData of capturedErrors) {
      const { dynamicComponents, errorMessage } = errorData;
      const resolvedComponents = [];
      for (const comp of dynamicComponents) {
        let pkg = comp.pkg; // Already resolved by resolveFullNameSync earlier
        if (pkg !== comp.name) {
          // Use actual resolution source (set in resolveFullNameSync)
          const source = comp.resolutionSource || "registry";
          console.log(
            "[hydration-tracking] Resolved by sync (" + source + "):",
            comp.name,
            "->",
            pkg
          );
        }
        resolvedComponents.push({ name: comp.name, pkg });
      }

      if (resolvedComponents.length === 0) continue;
      const rootCause = resolvedComponents[0];
      const hierarchy = buildHierarchy(resolvedComponents);

      allResolvedErrors.push({
        name: rootCause.name,
        source: rootCause.pkg,
        hierarchy: hierarchy,
        errorMessage: errorMessage,
        captureOrder: allResolvedErrors.length + 1
      });
    }

    if (allResolvedErrors.length === 0) return;
    const mainRootCauses = findIndependentRootCauses(allResolvedErrors);
    mainRootCauses.sort((a, b) => a.captureOrder - b.captureOrder);
    const primaryCause = mainRootCauses[0] || null;
    const cascadingCauses = mainRootCauses.slice(1);

    window.hydrationerror = {
      pageUrl: window.location.href,
      hasHydrationError: window.hydrationerror.hasHydrationError,
      primaryCause: primaryCause
        ? {
            name: primaryCause.name,
            source: primaryCause.source,
            hierarchy: primaryCause.hierarchy
          }
        : null,
      cascadingCauses: cascadingCauses.map((e) => ({
        name: e.name,
        source: e.source,
        hierarchy: e.hierarchy
      })),
      errorStack: window.hydrationerror.errorStack || []
    };
    if (primaryCause && typeof window.csLog === "function") {
      const hydrationErrorPayload = {
        eventName: "HYDRATION_ERROR",
        hydrationError: {
          cid: cid,
          url: window.location.href,
          hasHydrationError: window.hydrationerror.hasHydrationError,
          primaryCause: {
            name: primaryCause.name,
            source: primaryCause.source,
            hierarchy: primaryCause.hierarchy
          },
          cascadingCauses: cascadingCauses.map((e) => ({
            name: e.name,
            source: e.source,
            hierarchy: e.hierarchy
          })),
          totalCauses: mainRootCauses.length,
          errorMessage: primaryCause.errorMessage,
          errorStack: window.hydrationerror.errorStack || []
        }
      };

      window.csLog("error", hydrationErrorPayload);
      console.log("[hydration-tracking] Sent to Splunk");
    }
  };

  const originalHydrateRoot = ReactDOM.hydrateRoot;
  console.log("[hydration-tracking] Monkey-patch installed");

  ReactDOM.hydrateRoot = function (container, initialChildren, options) {
    return originalHydrateRoot(container, initialChildren, {
      ...options,
      onRecoverableError: (error, errInfo) => {
        const message = error.message || "";
        window.hydrationerror.hasHydrationError = true;

        if (!Array.isArray(window.hydrationerror.errorStack)) {
          window.hydrationerror.errorStack = [];
        }
        window.hydrationerror.errorStack.push({
          reactErrorCode: getReactErrorCode(message),
          componentStack: errInfo?.componentStack || null
        });

        if (errInfo?.componentStack) {
          const dynamicComponents = parseDynamicComponents(
            errInfo.componentStack
          );

          if (dynamicComponents.length > 0) {
            for (const comp of dynamicComponents) {
              // Preserve original stack trace pkg for source determination
              comp.originalPkg = comp.pkg;
              comp.pkg = resolveFullNameSync(comp);
            }
            const hierarchy = buildHierarchy(dynamicComponents);
            const primary = dynamicComponents[0];
            const cascading = dynamicComponents.slice(1);
            // Update at T0 (first error): set once so first hydration error is visible until processAndLog consolidates
            if (window.hydrationerror.primaryCause == null) {
              window.hydrationerror.primaryCause = primary
                ? {
                    name: primary.name,
                    source: primary.pkg || primary.name,
                    hierarchy
                  }
                : null;
              window.hydrationerror.cascadingCauses = cascading.map((c) => ({
                name: c.name,
                source: c.pkg || c.name,
                hierarchy
              }));
            }
            const errorKey = getErrorKey(dynamicComponents, message);
            if (!seenErrorKeys.has(errorKey)) {
              seenErrorKeys.add(errorKey);
              capturedErrors.push({
                dynamicComponents,
                errorMessage: message,
                timestamp: Date.now()
              });
            }
            if (processTimeout) clearTimeout(processTimeout);
            processTimeout = setTimeout(() => processAndLog(), 500);
          } else if (window.hydrationerror.primaryCause == null) {
            // Fallback: no next/dynamic wrapper found (e.g. malformed HTML in
            // an authored codeblock triggers a mismatch rooted at a plain
            // component like BodyContent). Use the 2nd frame from the
            // componentStack — with --no-mangling enabled in prod this yields
            // a real component name.
            const fallback = getSecondFrameFromComponentStack(
              errInfo.componentStack
            );
            const name = fallback?.name || "check locally for component name";
            const source =
              fallback?.location ||
              fallback?.name ||
              "check locally for component name";
            window.hydrationerror.primaryCause = {
              name,
              source,
              hierarchy: name
            };
            capturedErrors.push({
              dynamicComponents: [{ name, pkg: source }],
              errorMessage: message,
              timestamp: Date.now()
            });
            if (processTimeout) clearTimeout(processTimeout);
            processTimeout = setTimeout(() => processAndLog(), 500);
          }
        } else if (window.hydrationerror.primaryCause == null) {
          // No componentStack at all (React 18 prod without --no-mangling, or
          // other recoverable errors that don't ship a stack). Preserve the
          // existing behavior of surfacing a placeholder so the entry is at
          // least present in Splunk.
          window.hydrationerror.primaryCause = {
            name: "check locally for component name",
            source: "check locally for component name",
            hierarchy: "check locally for component name"
          };
          capturedErrors.push({
            dynamicComponents: [
              {
                name: "check locally for component name",
                pkg: "check locally for component name"
              }
            ],
            errorMessage: message,
            timestamp: Date.now()
          });
          if (processTimeout) clearTimeout(processTimeout);
          processTimeout = setTimeout(() => processAndLog(), 500);
        }

        options?.onRecoverableError?.(error, errInfo);
      }
    });
  };
}
/**
 * Error boundary that catches React render errors and prevents full page crashes.
 * On server side, re-throws to trigger Next.js error handling.
 * On client side, dispatches the error asynchronously so it surfaces as an unhandled exception.
 */
class RaaSErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    logException(error);
  }
  render() {
    if (this.state.hasError) return <NextErrorComponent />;
    return this.props.children;
  }
}

const logException = (error) => {
  if (typeof window === "undefined") {
    log.error(`EVENT_NAME=REACT_ERROR_BOUNDARY; Error message: ${error}`);
    throw error;
  }
  setTimeout(() => {
    throw error;
  }, 0);
};

function RenderingApp({ Component, pageProps }) {
  initializeOIL();

  // Read multiThemeEnabled from pageProps.metaData, set server-side via the
  // "multi_theme_enabled" site config key and enriched into metaData before SSR.
  // Using a server-side prop ensures SSR and client render identically, preventing hydration mismatches.
  const isMultiThemeEnabled = pageProps?.metaData?.multiThemeEnabled === true;

  // Filter out themes already mounted in the DOM to avoid duplicate CSS injection.
  // ThemeOrchestrationProvider injects a <style id="${theme}-tokens"> element per theme.
  const tokensUrlMap = useMemo(() => {
    const map = getTokensUrlMap();
    return Object.fromEntries(
      Object.entries(map).filter(([theme]) => !isThemeMounted(theme))
    );
  }, []);

  // Fallback URL used by ThemeOrchestrationProvider if the primary theme URL fails to load
  const fallbackUrl = useMemo(
    () => getFallbackThemeUrl(pageProps),
    [pageProps]
  );

  // After the page has loaded and hydration has completed, check whether any
  // unresolved ESI tag (e.g. <esi:include>) is present in the DOM. Its presence
  // means the edge did not process ESI; log it so it's visible.
  useEffect(() => {
    const scanForEsiTags = () => {
      // Presence check only: stop at the first ESI tag (tag name starts with
      // "esi"), whether or not it carries a src.
      const allElements = document.getElementsByTagName("*");
      let hasEsi = false;
      for (let i = 0; i < allElements.length; i++) {
        if (allElements[i].tagName.toLowerCase().startsWith("esi")) {
          hasEsi = true;
          break;
        }
      }
      if (!hasEsi) return;

      console.log("[esi-check] Unresolved ESI tag(s) present in the DOM");

      if (typeof window.csLog === "function") {
        // Mirror the cid resolution used by the hydration-error path.
        const cid =
          window.appVars?.pageContentId ||
          window.location.pathname
            .split("/")
            .find((p, i, a) => a[i - 1] === "preview" || a[i - 1] === "cas") ||
          "unknown";

        // Include the request logTrace (INTUIT_TID/REQUEST_ID/HOST/... string
        // built server-side) so the TID is available on the ESI Splunk logs.
        const logTrace =
          pageProps?.logTrace ||
          window.__NEXT_DATA__?.props?.pageProps?.logTrace;

        window.csLog("error", {
          eventName: "UNRESOLVED_ESI",
          unresolvedEsi: {
            cid: cid,
            url: window.location.href,
            hasEsi: true,
            logTrace: logTrace,
            pageTitle: document.title
          }
        });
        console.log("[esi-check] Sent to Splunk");
      }
    };

    // useEffect runs after hydration commits; additionally wait for full page
    // load so late-arriving markup is included in the scan.
    if (document.readyState === "complete") {
      scanForEsiTags();
      return undefined;
    }
    window.addEventListener("load", scanForEsiTags, { once: true });
    return () => window.removeEventListener("load", scanForEsiTags);
  }, []);

  if (isMultiThemeEnabled) {
    // Resolves from document.body or site config, depending on client/server.
    const theme = getAppTheme(pageProps);
    const colorScheme = getAppColorScheme(pageProps);

    const handleThemeLoadError = (error) => {
      log.error(`EVENT_NAME=THEME_LOAD_ERROR; Error: ${error}`);
    };

    return (
      <>
        <RaaSErrorBoundary>
          {/*
           * ThemeOrchestrationProvider manages loading all theme CSS URLs from tokensUrlMap.
           * It deduplicates CSS injection across nested ThemeProvider instances (e.g. Button).
           * The scoped ThemeProvider sets the page-level theme context for all child components.
           */}
          <ThemeOrchestrationProvider
            tokensUrlMap={tokensUrlMap}
            fallbackUrl={fallbackUrl}
            onErrorLoading={handleThemeLoadError}
          >
            <ThemeProvider scope theme={theme} colorScheme={colorScheme}>
              <Component {...pageProps} />
            </ThemeProvider>
          </ThemeOrchestrationProvider>
        </RaaSErrorBoundary>
      </>
    );
  }

  // Non-multi-theme path: render without any theme provider wrapping
  return (
    <>
      <RaaSErrorBoundary>
        <Component {...pageProps} />
      </RaaSErrorBoundary>
    </>
  );
}

// Uncomment if every page needs blocking data (disables static optimization)
RenderingApp.getInitialProps = async (appContext) => {
  const appProps = await App.getInitialProps(appContext);
  return { ...appProps };
};

export default RenderingApp;
